import { useState } from "react";
import axios from "axios";
import { Save, X, Plus, ChevronLeft, ChevronRight, PlusCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = { ground: "PCP/ACP-G", sfa_emr: "SFA/EMR", wes: "PCP-PS", air_adult: "Air Adult", air_pediatric: "OB/NEO/PEDS" };
const SERVICE_COLORS = { ground: "bg-blue-600", sfa_emr: "bg-emerald-600", wes: "bg-amber-600", air_adult: "bg-cyan-600", air_pediatric: "bg-pink-600" };

const CARE_LEVEL_CONFIG = {
  all_responders: { name: "All Responders", color: "border-l-gray-400 bg-gray-500/5" },
  sfa: { name: "Standard First Aid (SFA)", color: "border-l-sky-400 bg-sky-400/5" },
  emr: { name: "Emergency Medical Responder (EMR)", color: "border-l-emerald-700 bg-emerald-700/5" },
  pcp: { name: "Primary Care Paramedic (PCP)", color: "border-l-blue-600 bg-blue-600/5" },
  pcp_es: { name: "PCP – Extended Scope", color: "border-l-purple-500 bg-purple-500/5" },
  "pcp-es": { name: "PCP – Extended Scope", color: "border-l-purple-500 bg-purple-500/5" },
  wes: { name: "Paramedic Specialist (PCP-PS)", color: "border-l-orange-500 bg-orange-500/5" },
  paramedic_specialist: { name: "Paramedic Specialist", color: "border-l-orange-500 bg-orange-500/5" },
  acp: { name: "Advanced Care Paramedic (ACP)", color: "border-l-yellow-500 bg-yellow-500/5" },
  acp_g: { name: "Advanced Care Paramedic – Ground (ACP-G)", color: "border-l-yellow-500 bg-yellow-500/5" },
  acp_f: { name: "Advanced Care Paramedic – Flight (ACP-F)", color: "border-l-green-400 bg-green-400/5" },
  ccp: { name: "Critical Care Paramedic (CCP)", color: "border-l-red-500 bg-red-500/5" },
};

const ProtocolReviewMode = ({ protocolList, onExit, categories, token, onProtocolsChanged }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [data, setData] = useState(() => JSON.parse(JSON.stringify(protocolList[0])));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const update = (newData) => { setData(newData); setDirty(true); };

  const navigate = async (newIndex) => {
    if (dirty) {
      setSaving(true);
      try {
        await axios.put(`${API}/admin/protocols/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
        protocolList[currentIndex] = JSON.parse(JSON.stringify(data));
        toast.success("Saved");
        if (onProtocolsChanged) onProtocolsChanged();
      } catch { toast.error("Failed to save"); }
      setSaving(false);
    }
    setCurrentIndex(newIndex);
    setData(JSON.parse(JSON.stringify(protocolList[newIndex])));
    setDirty(false);
    window.scrollTo(0, 0);
  };

  const goNext = () => { if (currentIndex < protocolList.length - 1) navigate(currentIndex + 1); };
  const goPrev = () => { if (currentIndex > 0) navigate(currentIndex - 1); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/protocols/${data.id}`, data, { headers: { Authorization: `Bearer ${token}` } });
      protocolList[currentIndex] = JSON.parse(JSON.stringify(data));
      setDirty(false);
      toast.success("Saved!");
      if (onProtocolsChanged) onProtocolsChanged();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const updateIntText = (li, ii, v) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: [...d.care_levels[li].interventions]}; d.care_levels[li].interventions[ii] = {...d.care_levels[li].interventions[ii], text: v}; update(d); };
  const updateSubStep = (li, ii, si, v) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: [...d.care_levels[li].interventions]}; const subs = [...d.care_levels[li].interventions[ii].sub_steps]; subs[si] = v; d.care_levels[li].interventions[ii] = {...d.care_levels[li].interventions[ii], sub_steps: subs}; update(d); };
  const addIntervention = (li) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: [...d.care_levels[li].interventions, {text: "", sub_steps: []}]}; update(d); };
  const removeIntervention = (li, ii) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: d.care_levels[li].interventions.filter((_, i) => i !== ii)}; update(d); };
  const addSubStep = (li, ii) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: [...d.care_levels[li].interventions]}; d.care_levels[li].interventions[ii] = {...d.care_levels[li].interventions[ii], sub_steps: [...(d.care_levels[li].interventions[ii].sub_steps || []), ""]}; update(d); };
  const removeSubStep = (li, ii, si) => { const d = {...data}; d.care_levels = [...d.care_levels]; d.care_levels[li] = {...d.care_levels[li], interventions: [...d.care_levels[li].interventions]}; d.care_levels[li].interventions[ii] = {...d.care_levels[li].interventions[ii], sub_steps: d.care_levels[li].interventions[ii].sub_steps.filter((_, i) => i !== si)}; update(d); };
  const updateInfoBox = (i, f, v) => { const boxes = [...(data.info_boxes || [])]; boxes[i] = {...boxes[i], [f]: v}; update({...data, info_boxes: boxes}); };
  const removeInfoBox = (i) => update({...data, info_boxes: (data.info_boxes || []).filter((_, j) => j !== i)});
  const addInfoBox = () => update({...data, info_boxes: [...(data.info_boxes || []), {type: "info", title: "New Note", content: ""}]});

  const careLevels = data.care_levels || [];

  return (
    <div className="min-h-screen bg-background" data-testid="review-mode">
      <header className="border-b bg-card sticky top-0 z-20">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { if (dirty) handleSave().then(onExit); else onExit(); }} data-testid="exit-review-btn">
              <X className="w-4 h-4 mr-1" />Exit
            </Button>
            <Button variant="ghost" size="sm" onClick={goPrev} disabled={currentIndex === 0 || saving} data-testid="review-prev-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <select
              value={currentIndex}
              onChange={(e) => navigate(Number(e.target.value))}
              className="bg-background border rounded px-2 py-1 text-sm max-w-[280px] font-medium"
              data-testid="review-jump-select"
            >
              {protocolList.map((p, i) => (
                <option key={p.id} value={i}>{i + 1}. {p.name}</option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={goNext} disabled={currentIndex === protocolList.length - 1 || saving} data-testid="review-next-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
            <Badge variant="outline" className="font-mono text-xs">{currentIndex + 1}/{protocolList.length}</Badge>
          </div>
          <Button size="sm" onClick={handleSave} disabled={!dirty || saving} className={dirty ? "bg-green-600 hover:bg-green-700" : ""} data-testid="review-save-btn">
            <Save className="w-4 h-4 mr-1" />{saving ? "Saving..." : dirty ? "Save Changes" : "Saved"}
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        <div className="flex items-center gap-3">
          <Badge className={`${SERVICE_COLORS[data.service] || "bg-zinc-600"} text-white text-xs shrink-0`}>{SERVICE_LABELS[data.service] || data.service}</Badge>
          <input
            value={data.name}
            onChange={(e) => update({...data, name: e.target.value})}
            className="font-heading text-2xl font-bold bg-transparent border-b border-transparent hover:border-zinc-600 focus:border-primary focus:outline-none flex-1 py-1"
            data-testid="review-protocol-name"
          />
        </div>

        {careLevels.map((level, li) => {
          const cfg = CARE_LEVEL_CONFIG[level.level] || { name: level.title, color: "border-l-gray-400 bg-gray-500/5" };
          return (
            <Card key={li} className={`border-l-4 ${cfg.color}`}>
              <CardHeader className="py-3 pb-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-base font-semibold">{cfg.name}</h3>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground" onClick={() => addIntervention(li)}>
                    <Plus className="w-3 h-3 mr-1" />Add Step
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0 pb-3">
                {(level.interventions || []).map((iv, ii) => (
                  <div key={ii} className="group py-1.5 border-b border-border/30 last:border-0">
                    <div className="flex items-start gap-2">
                      <span className="text-muted-foreground text-xs pt-1.5 w-4 text-right shrink-0">{ii + 1}.</span>
                      <textarea
                        value={iv.text}
                        onChange={(e) => updateIntText(li, ii, e.target.value)}
                        className="flex-1 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-primary focus:outline-none text-sm py-1 resize-none min-h-[28px]"
                        rows={Math.max(1, Math.ceil((iv.text || "").length / 90))}
                      />
                      <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity pt-1">
                        <button className="h-6 w-6 flex items-center justify-center rounded hover:bg-muted text-muted-foreground" title="Add sub-step" onClick={() => addSubStep(li, ii)}><PlusCircle className="w-3 h-3" /></button>
                        <button className="h-6 w-6 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive" onClick={() => removeIntervention(li, ii)}><Trash2 className="w-3 h-3" /></button>
                      </div>
                    </div>
                    {(iv.sub_steps || []).length > 0 && (
                      <div className="ml-8 mt-0.5 space-y-0.5">
                        {iv.sub_steps.map((step, si) => (
                          <div key={si} className="flex items-center gap-1.5 group/sub">
                            <span className="text-muted-foreground text-xs">-</span>
                            <input
                              value={step}
                              onChange={(e) => updateSubStep(li, ii, si, e.target.value)}
                              className="flex-1 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-primary focus:outline-none text-sm text-muted-foreground py-0.5"
                            />
                            <button className="h-5 w-5 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive opacity-0 group-hover/sub:opacity-100" onClick={() => removeSubStep(li, ii, si)}><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {(!level.interventions || level.interventions.length === 0) && (
                  <p className="text-muted-foreground text-sm text-center py-3">No interventions — <button className="text-primary underline" onClick={() => addIntervention(li)}>add one</button></p>
                )}
              </CardContent>
            </Card>
          );
        })}

        {(data.info_boxes || []).length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">Notes & Warnings</h3>
              <Button size="sm" variant="ghost" className="text-xs" onClick={addInfoBox}><Plus className="w-3 h-3 mr-1" />Add</Button>
            </div>
            {data.info_boxes.map((box, i) => (
              <Card key={i} className={`border-l-4 ${box.type === 'warning' ? 'border-l-amber-500' : box.type === 'critical' ? 'border-l-red-500' : 'border-l-blue-500'} group`}>
                <CardContent className="py-3">
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-1">
                      <div className="flex gap-2 items-center">
                        <select value={box.type} onChange={(e) => updateInfoBox(i, 'type', e.target.value)} className="px-1.5 py-0.5 rounded border bg-background text-xs">
                          <option value="info">Info</option><option value="warning">Warning</option><option value="critical">Critical</option>
                        </select>
                        <input value={box.title} onChange={(e) => updateInfoBox(i, 'title', e.target.value)} className="flex-1 bg-transparent border-b border-transparent hover:border-zinc-700 focus:border-primary focus:outline-none text-sm font-semibold" />
                      </div>
                      <textarea value={box.content} onChange={(e) => updateInfoBox(i, 'content', e.target.value)} className="w-full bg-transparent border border-transparent hover:border-zinc-700 focus:border-primary focus:outline-none rounded p-1 text-sm min-h-[40px]" rows={Math.max(2, (box.content || '').split('\n').length)} />
                    </div>
                    <button className="h-6 w-6 flex items-center justify-center rounded hover:bg-destructive/20 text-destructive opacity-0 group-hover:opacity-100" onClick={() => removeInfoBox(i)}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        {(data.info_boxes || []).length === 0 && (
          <button className="text-sm text-muted-foreground hover:text-primary flex items-center gap-1" onClick={addInfoBox}><Plus className="w-3 h-3" />Add Note/Warning</button>
        )}

        {(data.references || []).length > 0 && (
          <div>
            <h3 className="font-heading text-lg font-semibold mb-2">Related Protocols</h3>
            <div className="flex flex-wrap gap-2">
              {data.references.map(ref => (
                <Badge key={ref} variant="outline" className="text-xs">{ref}</Badge>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between items-center pt-6 border-t">
          <Button variant="outline" onClick={goPrev} disabled={currentIndex === 0 || saving}>
            <ChevronLeft className="w-4 h-4 mr-1" />Previous
          </Button>
          <span className="text-sm text-muted-foreground">{currentIndex + 1} of {protocolList.length}</span>
          <Button variant="outline" onClick={goNext} disabled={currentIndex === protocolList.length - 1 || saving}>
            Next<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProtocolReviewMode;
